import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { AdminGuard } from "@/components/admin/AdminShell";

const CAT_OBJECTS = {
  salon: [["Canapé 3 places", 2.5], ["Canapé 2 places", 2], ["Fauteuil", 1], ["Table basse", 0.5], ["Meuble TV", 0.8], ["Bibliothèque", 1.5]],
  chambre: [["Lit double", 2], ["Lit simple", 1.2], ["Matelas", 1], ["Armoire 2 portes", 2], ["Armoire 3 portes", 3], ["Commode", 0.8]],
  cuisine: [["Réfrigérateur", 0.8], ["Frigo américain", 1.5], ["Lave-linge", 0.6], ["Lave-vaisselle", 0.6], ["Cuisinière", 0.6], ["Table cuisine", 1]],
  bureau: [["Bureau", 1], ["Chaise de bureau", 0.4], ["Caisson", 0.3], ["Étagère", 0.8]],
  autres: [["Carton standard", 0.1], ["Valise", 0.25], ["Piano droit", 2], ["Coffre-fort", 0.5], ["Objet volumineux", 1.5]],
};

export default function InventoryPage() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    apiClient.get(`/admin/cases/${id}/inventory`).then((r) => setInv(r.data)).catch(() => toast.error("Erreur"));
    apiClient.get(`/admin/cases/${id}/completeness`).then((r) => setCompleteness(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async (payload, msg = "Enregistré") => {
    setSaving(true);
    try {
      const r = await apiClient.put(`/admin/cases/${id}/inventory`, payload);
      setInv((prev) => ({ ...prev, ...r.data }));
      toast.success(msg);
      apiClient.get(`/admin/cases/${id}/completeness`).then((res) => setCompleteness(res.data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!inv) return <AdminGuard><div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" /></div></AdminGuard>;

  const items = inv.items || [];
  const addItem = (category, label, vol) => save({ items: [...items, { category, label, quantity: 1, unit_volume_m3: vol }] }, "Objet ajouté");
  const updateQty = (idx, qty) => {
    const next = items.map((it, i) => (i === idx ? { ...it, quantity: Math.max(0, qty) } : it)).filter((it) => it.quantity > 0);
    save({ items: next });
  };
  const overrideVolume = async () => {
    const value = window.prompt("Volume retenu (m³)", inv.retained_volume_m3);
    if (!value) return;
    const justification = window.prompt("Justification (obligatoire)");
    if (!justification) return toast.error("Justification obligatoire");
    try {
      const r = await apiClient.post(`/admin/cases/${id}/inventory/override-volume`, { value: parseFloat(value), justification });
      setInv((prev) => ({ ...prev, ...r.data }));
      toast.success("Volume ajusté");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const AccessForm = ({ which, label }) => {
    const a = inv[which] || {};
    const setA = (field, value) => setInv((p) => ({ ...p, [which]: { ...(p[which] || {}), [field]: value } }));
    return (
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <h3 className="text-xs uppercase tracking-widest text-orange font-bold mb-2">{label}</h3>
        <div className="grid grid-cols-2 gap-2">
          {[["floor", "Étage"], ["elevator", "Ascenseur (oui/non/dimensions)"], ["carry_distance_m", "Distance portage (m)"],
            ["stairs", "Escaliers"], ["parking", "Stationnement"], ["door_code", "Digicode"], ["onsite_contact", "Contact sur place"], ["access_hours", "Horaires d'accès"]].map(([f, l]) => (
            <div key={f}>
              <label className="text-[11px] font-semibold text-navy">{l}</label>
              <Input data-testid={`${which}-${f}`} value={a[f] || ""} onChange={(e) => setA(f, e.target.value)} className="h-8 bg-white" />
            </div>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs mt-2">
          <input type="checkbox" checked={a.furniture_lift_needed || false} onChange={(e) => setA("furniture_lift_needed", e.target.checked)} />
          Monte-meubles nécessaire
        </label>
        <Textarea placeholder="Notes d'accès" value={a.notes || ""} onChange={(e) => setA("notes", e.target.value)} rows={2} className="mt-2 bg-white text-sm" />
        <Button data-testid={`${which}-save`} onClick={() => save({ [which === "access_origin" ? "access_origin" : "access_destination"]: a }, "Accès enregistrés")} size="sm" className="bg-navy text-white rounded-lg mt-2" disabled={saving}>Enregistrer</Button>
      </div>
    );
  };

  const trip = inv.trip || {};
  return (
    <AdminGuard>
      <div className="flex items-center gap-3 mb-4">
        <h1 data-testid="inventory-header" className="font-heading font-extrabold text-2xl text-navy mr-auto">Inventaire &amp; évaluation</h1>
        <Link to={`/admin/dossiers/${id}`} className="text-sm underline text-navy">← Retour au dossier</Link>
      </div>

      {completeness && (
        <div data-testid="completeness-banner" className={`rounded-xl px-4 py-3 mb-4 text-sm ${completeness.complete ? "bg-green-50 text-green-800" : "bg-orange/10 text-navy"}`}>
          {completeness.complete ? "✓ Dossier complet pour établir un devis" :
            <>Informations manquantes : <strong>{completeness.missing_labels.join(" • ")}</strong></>}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <h3 className="text-xs uppercase tracking-widest text-orange font-bold mb-3">Objets ({items.length})</h3>
            {items.map((it, idx) => (
              <div key={it.id || idx} className="flex items-center gap-2 py-1.5 border-b last:border-0" data-testid={`inv-item-${idx}`}>
                <span className="flex-1 text-sm">{it.label} <span className="text-xs text-muted-foreground">({inv.category_labels?.[it.category] || it.category})</span></span>
                <button onClick={() => updateQty(idx, (it.quantity || 1) - 1)} className="w-7 h-7 rounded-full bg-graylight font-bold">−</button>
                <span className="w-6 text-center text-sm font-semibold">{it.quantity}</span>
                <button onClick={() => updateQty(idx, (it.quantity || 1) + 1)} className="w-7 h-7 rounded-full bg-graylight font-bold">+</button>
                <span className="text-xs w-16 text-right">{(it.total_volume_m3 || 0).toFixed(1)} m³</span>
                <button data-testid={`inv-item-remove-${idx}`} onClick={() => updateQty(idx, 0)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <div className="mt-3">
              {Object.entries(CAT_OBJECTS).map(([cat, objs]) => (
                <div key={cat} className="mb-2">
                  <div className="text-[11px] uppercase font-bold text-muted-foreground">{inv.category_labels?.[cat] || cat}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {objs.map(([label, vol]) => (
                      <button key={label} data-testid={`inv-add-${label.replace(/\s/g, "-")}`} onClick={() => addItem(cat, label, vol)}
                        className="text-xs bg-graylight hover:bg-navy hover:text-white rounded-full px-2.5 py-1 transition-colors">
                        <Plus className="w-3 h-3 inline" /> {label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <AccessForm which="access_origin" label="Accès au départ" />
            <AccessForm which="access_destination" label="Accès à l'arrivée" />
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-4">
            <h3 className="text-xs uppercase tracking-widest text-orange font-bold mb-2">Trajet</h3>
            {inv.maps && !inv.maps.configured && <div className="text-xs text-muted-foreground mb-2">{inv.maps.message}</div>}
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[11px] font-semibold text-navy">Distance (km)</label>
                <Input data-testid="trip-distance" type="number" value={trip.distance_km || ""} onChange={(e) => setInv((p) => ({ ...p, trip: { ...trip, distance_km: parseFloat(e.target.value) || null } }))} className="h-8 bg-white" /></div>
              <div><label className="text-[11px] font-semibold text-navy">Durée (min)</label>
                <Input data-testid="trip-duration" type="number" value={trip.duration_min || ""} onChange={(e) => setInv((p) => ({ ...p, trip: { ...trip, duration_min: parseFloat(e.target.value) || null } }))} className="h-8 bg-white" /></div>
            </div>
            <Button data-testid="trip-save" onClick={() => save({ trip }, "Trajet enregistré")} size="sm" className="bg-navy text-white rounded-lg mt-2" disabled={saving}>Enregistrer</Button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <h3 className="text-xs uppercase tracking-widest text-orange font-bold mb-2">Volume</h3>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Calculé</span><span data-testid="volume-computed" className="font-semibold">{inv.computed_volume_m3} m³</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Marge de sécurité</span><span>{inv.safety_margin_pct} %</span></div>
              <div className="flex justify-between text-base font-extrabold text-navy"><span>Retenu</span><span data-testid="volume-retained">{inv.retained_volume_m3} m³</span></div>
              {inv.volume_override && <div className="text-xs text-amber-700 pt-1">Ajusté manuellement : {inv.volume_override.justification}</div>}
            </div>
            <div className="flex gap-2 mt-3">
              <Button data-testid="volume-override" onClick={overrideVolume} size="sm" variant="outline" className="rounded-lg">Ajuster</Button>
              <Button data-testid="volume-margin" onClick={() => { const m = window.prompt("Marge de sécurité (%)", inv.safety_margin_pct); if (m !== null) save({ safety_margin_pct: parseFloat(m) || 0 }); }} size="sm" variant="outline" className="rounded-lg">Marge</Button>
            </div>
          </div>
          {Object.keys(inv.by_category || {}).length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <h3 className="text-xs uppercase tracking-widest text-orange font-bold mb-2">Par pièce</h3>
              {Object.entries(inv.by_category).map(([c, v]) => (
                <div key={c} className="flex justify-between text-sm py-0.5"><span>{inv.category_labels?.[c] || c}</span><span className="font-semibold">{v} m³</span></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminGuard>
  );
}
